import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { electricalApi } from '../../lib/api';
import { toast } from 'sonner';
import { Zap, AlertTriangle, Loader2, Plus, Trash2, ChevronDown, ChevronRight, RotateCcw, ArrowRightLeft } from 'lucide-react';

const euro = (n) => `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true })}`;

const STEPS = ['Tipo intervento', 'Tecnologia', 'Serie civile', 'Modalità', 'Configurazione', 'Verifica'];

export const ElectricalWizardModal = ({ open, onClose, estimateId, onApplied }) => {
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const [interventionType, setInterventionType] = useState('nuovo');
  const [technology, setTechnology] = useState('standard');
  const [series, setSeries] = useState('plana');
  const [mode, setMode] = useState('preset');
  const [preset, setPreset] = useState('trivani');
  const [options, setOptions] = useState({ cucina_induzione: true, include_boiler: false, include_caldaia: false, include_citofono: false });
  const [includeGateway, setIncludeGateway] = useState(true);
  const [manualItems, setManualItems] = useState([]);
  const [manualCode, setManualCode] = useState('');
  const [manualQty, setManualQty] = useState(1);

  // FASE 5 - Step 6: configurazione editabile localmente (nessuna persistenza fino ad Applica)
  const [editLines, setEditLines] = useState([]);
  const [presetLines, setPresetLines] = useState([]);
  const [expandedRooms, setExpandedRooms] = useState({});
  const [addSel, setAddSel] = useState({});

  useEffect(() => {
    if (open && !config) {
      setLoading(true);
      electricalApi.getConfig()
        .then((r) => setConfig(r.data))
        .catch(() => toast.error('Impossibile caricare la configurazione elettrica'))
        .finally(() => setLoading(false));
    }
    if (open) { setStep(0); setPreview(null); setEditLines([]); setPresetLines([]); }
  }, [open]); // eslint-disable-line

  const buildPayload = () => ({
    intervention_type: interventionType,
    technology,
    series,
    mode,
    preset: mode === 'preset' ? preset : 'personalizzato',
    options,
    manual_items: manualItems,
    include_gateway: includeGateway,
  });

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await electricalApi.preview(buildPayload());
      setPreview(r.data);
      const lines = initEditLines(r.data.lines || []);
      setEditLines(lines);
      setPresetLines(lines.map((l) => ({ ...l })));
      const exp = {};
      lines.forEach((l) => { exp[l.room_key] = true; });
      setExpandedRooms(exp);
    } catch (e) {
      toast.error('Errore nel calcolo anteprima');
    } finally {
      setLoading(false);
    }
  };

  const goNext = async () => {
    const next = step + 1;
    if (next === 5) { await runPreview(); }
    setStep(next);
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const explicit_lines = editLines
        .filter((l) => !l.removed && l.quantity > 0)
        .map((l) => ({
          code: l.code,
          quantity: l.quantity,
          room_key: l.room_key,
          room_label: l.room_label,
          scope: l.scope,
          functional_label: l.functional_label || null,
        }));
      const r = await electricalApi.apply(estimateId, { ...buildPayload(), explicit_lines });
      toast.success('Impianto elettrico applicato al preventivo');
      onApplied && onApplied(r.data);
      onClose();
    } catch (e) {
      toast.error('Errore durante l\'applicazione');
    } finally {
      setApplying(false);
    }
  };

  const catalog = config?.catalog || [];
  const addManual = () => {
    if (!manualCode) return;
    setManualItems((prev) => [...prev, { code: manualCode, quantity: Number(manualQty) || 1 }]);
    setManualCode(''); setManualQty(1);
  };
  const removeManual = (i) => setManualItems((prev) => prev.filter((_, idx) => idx !== i));
  const codeDesc = useMemo(() => Object.fromEntries(catalog.map((c) => [c.code, c.description])), [catalog]);

  // === FASE 5 - Step 6 helpers (editing locale, prezzi dal catalogo) ===
  const computeUnitPrice = (voce, tech, ser) => {
    if (!voce) return 0;
    const m = voce.pricing_mode;
    const sp = voce.standard_prices || {};
    const cp = voce.connected_prices || {};
    if (m === 'fixed' || m === 'fixed_connected_only') return Number(voce.fixed_price || 0);
    if (m === 'series') return Number(sp[ser] || 0);
    if (m === 'series_connected') return tech === 'connesso' ? Number(cp[ser] || 0) : Number(sp[ser] || 0);
    if (m === 'connected_only_series') return Number(cp[ser] || 0);
    return Number(voce.unit_price || 0);
  };
  const initEditLines = (lines) => lines.map((l, i) => ({
    uid: `${l.code}-${i}`,
    code: l.code,
    description: l.description,
    functional_label: l.functional_label || null,
    room_key: l.electrical_room_key || 'altre',
    room_label: l.electrical_room_label || 'Altre lavorazioni elettriche',
    scope: l.electrical_scope || 'general',
    unit_price: Number(l.unit_price || 0),
    quantity: Number(l.quantity || 0),
    removed: false,
  }));
  const groups = useMemo(() => {
    const map = new Map();
    editLines.forEach((l) => {
      if (!map.has(l.room_key)) map.set(l.room_key, { key: l.room_key, label: l.room_label, lines: [] });
      map.get(l.room_key).lines.push(l);
    });
    const rank = (k) => (k === 'generali' ? 3 : k === 'altre' ? 2 : 1);
    return Array.from(map.values()).sort((a, b) => rank(a.key) - rank(b.key));
  }, [editLines]);
  const roomOptions = useMemo(() => groups.map((g) => ({ key: g.key, label: g.label })), [groups]);
  const groupSubtotal = (g) => g.lines.filter((l) => !l.removed).reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const configTotal = useMemo(() => editLines.filter((l) => !l.removed).reduce((s, l) => s + l.quantity * l.unit_price, 0), [editLines]);
  const currentQty = useMemo(() => editLines.filter((l) => !l.removed).reduce((s, l) => s + l.quantity, 0), [editLines]);
  const presetTotalQty = useMemo(() => presetLines.reduce((s, l) => s + Number(l.quantity || 0), 0), [presetLines]);
  const belowPreset = presetLines.length > 0 && currentQty < presetTotalQty;

  const setQty = (uid, q) => setEditLines((prev) => prev.map((l) => (l.uid === uid ? { ...l, quantity: Math.max(0, q) } : l)));
  const bump = (uid, d) => setEditLines((prev) => prev.map((l) => (l.uid === uid ? { ...l, quantity: Math.max(0, Math.round((l.quantity + d) * 100) / 100) } : l)));
  const toggleRemove = (uid, val) => setEditLines((prev) => prev.map((l) => (l.uid === uid ? { ...l, removed: val } : l)));
  const moveRoom = (uid, roomKey) => setEditLines((prev) => prev.map((l) => {
    if (l.uid !== uid) return l;
    const g = groups.find((x) => x.key === roomKey);
    return { ...l, room_key: roomKey, room_label: g ? g.label : roomKey, scope: (roomKey === 'generali' || roomKey === 'altre') ? 'general' : 'room' };
  }));
  const addToRoom = (roomKey) => {
    const code = addSel[roomKey];
    if (!code) return;
    const voce = catalog.find((c) => c.code === code);
    if (!voce) return;
    const g = groups.find((x) => x.key === roomKey);
    setEditLines((prev) => [...prev, {
      uid: `add-${code}-${Date.now()}`,
      code,
      description: voce.description,
      functional_label: null,
      room_key: roomKey,
      room_label: g ? g.label : roomKey,
      scope: (roomKey === 'generali' || roomKey === 'altre') ? 'general' : 'room',
      unit_price: computeUnitPrice(voce, technology, series),
      quantity: 1,
      removed: false,
    }]);
    setAddSel((s) => ({ ...s, [roomKey]: '' }));
  };
  const restorePreset = () => {
    if (editLines.length && !window.confirm('Ripristinare i valori del preset? Le modifiche locali verranno perse.')) return;
    setEditLines(presetLines.map((l) => ({ ...l })));
  };

  const canProceed = step < 4 || (mode === 'preset' ? !!preset : manualItems.length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl h-[88vh] overflow-hidden flex flex-col" data-testid="electrical-wizard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-orange-500" /> Preventivatore impianto elettrico
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 text-xs flex-wrap">
          {STEPS.map((s, i) => (
            <div key={s} className={`px-2 py-1 rounded ${i === step ? 'bg-orange-500 text-white' : i < step ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>
              {i + 1}. {s}
            </div>
          ))}
        </div>
        <Separator />

        <div className="flex-1 min-h-0 overflow-y-auto pr-3" data-testid="wizard-scroll-area">
          {loading && !config ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
          ) : (
            <div className="py-3 space-y-4">
              {step === 0 && (
                <div data-testid="step-intervention">
                  <p className="text-sm text-gray-500 mb-3">Seleziona il tipo di intervento. Determina il capitolato generale inserito una sola volta nel preventivo.</p>
                  <RadioGroup value={interventionType} onValueChange={setInterventionType}>
                    <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer" data-testid="opt-nuovo">
                      <RadioGroupItem value="nuovo" id="it-nuovo" />
                      <div><div className="font-medium">Nuovo impianto</div><div className="text-xs text-gray-500">Realizzazione di un impianto elettrico completo.</div></div>
                    </label>
                    <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer" data-testid="opt-modifiche">
                      <RadioGroupItem value="modifiche" id="it-mod" />
                      <div><div className="font-medium">Modifiche / integrazioni impianto esistente</div><div className="text-xs text-gray-500">Solo le lavorazioni indicate; nessun preset obbligatorio.</div></div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-technology">
                  <RadioGroup value={technology} onValueChange={setTechnology}>
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" data-testid="opt-standard">
                      <RadioGroupItem value="standard" id="tech-std" /><div className="font-medium">Standard</div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" data-testid="opt-connesso">
                      <RadioGroupItem value="connesso" id="tech-conn" />
                      <div><div className="font-medium">Connesso</div><div className="text-xs text-gray-500">Aggiunge automaticamente il Gateway Vimar View Wireless (D07.001).</div></div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-series">
                  <RadioGroup value={series} onValueChange={setSeries}>
                    {(config?.series || []).map((s) => (
                      <label key={s.code} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" data-testid={`opt-series-${s.code}`}>
                        <RadioGroupItem value={s.code} id={`ser-${s.code}`} /><div className="font-medium">{s.label}</div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-mode">
                  <RadioGroup value={mode} onValueChange={setMode}>
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" data-testid="opt-preset">
                      <RadioGroupItem value="preset" id="mode-preset" /><div><div className="font-medium">Preset</div><div className="text-xs text-gray-500">Dotazione tipo per taglio alloggio.</div></div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer" data-testid="opt-manuale">
                      <RadioGroupItem value="manuale" id="mode-man" /><div><div className="font-medium">Configurazione manuale</div><div className="text-xs text-gray-500">Costruisci l'impianto voce per voce.</div></div>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {step === 4 && mode === 'preset' && (
                <div data-testid="step-config-preset" className="space-y-4">
                  <div>
                    <Label className="text-sm">Preset</Label>
                    <RadioGroup value={preset} onValueChange={setPreset} className="grid grid-cols-2 gap-2 mt-2">
                      {(config?.presets || []).filter(p => p.code !== 'personalizzato').map((p) => (
                        <label key={p.code} className="flex items-center gap-2 p-2 border rounded cursor-pointer" data-testid={`opt-preset-${p.code}`}>
                          <RadioGroupItem value={p.code} id={`pr-${p.code}`} />{p.label}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <Label className="text-sm">Opzioni</Label>
                    <label className="flex items-center gap-2 text-sm" data-testid="opt-induzione">
                      <Checkbox checked={options.cucina_induzione} onCheckedChange={(v) => setOptions(o => ({ ...o, cucina_induzione: !!v }))} />
                      Cucina a induzione (aggiunge linea D05.002)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={options.include_boiler} onCheckedChange={(v) => setOptions(o => ({ ...o, include_boiler: !!v }))} />
                      Boiler / scaldacqua (D05.005)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={options.include_caldaia} onCheckedChange={(v) => setOptions(o => ({ ...o, include_caldaia: !!v }))} />
                      Alimentazione caldaia (D05.001)
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={options.include_citofono} onCheckedChange={(v) => setOptions(o => ({ ...o, include_citofono: !!v }))} />
                      Predisposizione citofono/videocitofono (D03.004)
                    </label>
                    {technology === 'connesso' && (
                      <label className="flex items-center gap-2 text-sm" data-testid="opt-gateway">
                        <Checkbox checked={includeGateway} onCheckedChange={(v) => setIncludeGateway(!!v)} />
                        Includi Gateway connesso (D07.001)
                      </label>
                    )}
                  </div>
                </div>
              )}

              {step === 4 && mode === 'manuale' && (
                <div data-testid="step-config-manual" className="space-y-3">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Voce</Label>
                      <Select value={manualCode} onValueChange={setManualCode}>
                        <SelectTrigger data-testid="manual-code-select"><SelectValue placeholder="Seleziona voce D" /></SelectTrigger>
                        <SelectContent>
                          {catalog.map((c) => (<SelectItem key={c.code} value={c.code}>{c.code} — {c.description.slice(0, 50)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-20"><Label className="text-xs">Q.tà</Label><Input type="number" min="1" value={manualQty} onChange={(e) => setManualQty(e.target.value)} data-testid="manual-qty" /></div>
                    <Button type="button" onClick={addManual} data-testid="manual-add-btn"><Plus className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1">
                    {manualItems.length === 0 && <p className="text-xs text-gray-400">Nessuna voce aggiunta.</p>}
                    {manualItems.map((mi, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border rounded px-2 py-1">
                        <span>{mi.quantity}× {mi.code} — {(codeDesc[mi.code] || '').slice(0, 40)}</span>
                        <button onClick={() => removeManual(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                  {technology === 'connesso' && (
                    <label className="flex items-center gap-2 text-sm" data-testid="opt-gateway-manual">
                      <Checkbox checked={includeGateway} onCheckedChange={(v) => setIncludeGateway(!!v)} />
                      Includi Gateway connesso (D07.001)
                    </label>
                  )}
                </div>
              )}

              {step === 5 && (
                <div data-testid="step-review" className="space-y-3">
                  {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>
                  ) : preview ? (
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex flex-wrap gap-2 text-xs">
                          <Badge variant="outline">{interventionType === 'nuovo' ? 'Nuovo impianto' : 'Modifiche'}</Badge>
                          <Badge variant="outline">{technology}</Badge>
                          <Badge variant="outline">{(config?.series || []).find(s => s.code === series)?.label}</Badge>
                          <Badge variant="outline">{mode === 'preset' ? preset : 'manuale'}</Badge>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={restorePreset} data-testid="wizard-restore-preset-btn">
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Ripristina valori preset
                        </Button>
                      </div>

                      {(preview.warnings || []).map((w, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs bg-amber-50 text-amber-800 rounded p-2" data-testid="wizard-warning">
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{w}</span>
                        </div>
                      ))}
                      {belowPreset && (
                        <div className="flex items-start gap-2 text-xs bg-blue-50 text-blue-800 rounded p-2" data-testid="wizard-below-preset">
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                          <span>Dotazione inferiore al preset Abitalo – verificare la scelta prima dell'applicazione.</span>
                        </div>
                      )}

                      <div className="space-y-2">
                        {groups.map((g) => {
                          const isOpen = expandedRooms[g.key] !== false;
                          const activeCount = g.lines.filter(l => !l.removed).length;
                          return (
                            <div key={g.key} className="border rounded-lg overflow-hidden" data-testid={`wizard-group-${g.key}`}>
                              <button type="button" onClick={() => setExpandedRooms(s => ({ ...s, [g.key]: !isOpen }))}
                                className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100"
                                data-testid={`wizard-group-toggle-${g.key}`}>
                                <span className="flex items-center gap-2 font-medium text-sm">
                                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  {g.label}
                                  <Badge variant="secondary" className="text-[10px]">{activeCount}</Badge>
                                </span>
                                <span className="text-sm font-semibold text-orange-600" data-testid={`wizard-group-subtotal-${g.key}`}>{euro(groupSubtotal(g))}</span>
                              </button>
                              {isOpen && (
                                <div className="divide-y">
                                  {g.lines.map((l) => (
                                    <div key={l.uid} className={`flex items-center gap-2 px-3 py-2 text-xs ${l.removed ? 'opacity-40' : ''}`} data-testid={`wizard-line-${l.code}`}>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-mono text-[10px] text-gray-400">{l.code}</div>
                                        <div className="truncate">{l.functional_label || l.description}</div>
                                      </div>
                                      {!l.removed ? (
                                        <>
                                          <div className="flex items-center gap-1">
                                            <button type="button" onClick={() => bump(l.uid, -1)} className="w-6 h-6 border rounded hover:bg-gray-100" data-testid={`wizard-qty-dec-${l.code}`}>−</button>
                                            <Input type="number" value={l.quantity} min="0" onChange={(e) => setQty(l.uid, Number(e.target.value))}
                                              className="w-14 h-7 text-center px-1" data-testid={`wizard-qty-${l.code}`} />
                                            <button type="button" onClick={() => bump(l.uid, 1)} className="w-6 h-6 border rounded hover:bg-gray-100" data-testid={`wizard-qty-inc-${l.code}`}>+</button>
                                          </div>
                                          <div className="w-16 text-right">{euro(l.unit_price)}</div>
                                          <div className="w-20 text-right font-medium" data-testid={`wizard-line-total-${l.code}`}>{euro(l.quantity * l.unit_price)}</div>
                                          <Select value={l.room_key} onValueChange={(v) => moveRoom(l.uid, v)}>
                                            <SelectTrigger className="w-8 h-7 p-0 justify-center" data-testid={`wizard-move-${l.code}`}><ArrowRightLeft className="h-3.5 w-3.5" /></SelectTrigger>
                                            <SelectContent>{roomOptions.map(r => (<SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>))}</SelectContent>
                                          </Select>
                                          <button type="button" onClick={() => toggleRemove(l.uid, true)} className="text-red-500" data-testid={`wizard-remove-${l.code}`}><Trash2 className="h-4 w-4" /></button>
                                        </>
                                      ) : (
                                        <button type="button" onClick={() => toggleRemove(l.uid, false)} className="text-blue-600 underline" data-testid={`wizard-restore-${l.code}`}>Ripristina voce</button>
                                      )}
                                    </div>
                                  ))}
                                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50/60">
                                    <Select value={addSel[g.key] || ''} onValueChange={(v) => setAddSel(s => ({ ...s, [g.key]: v }))}>
                                      <SelectTrigger className="flex-1 h-7 text-xs" data-testid={`wizard-add-select-${g.key}`}><SelectValue placeholder="Aggiungi voce D…" /></SelectTrigger>
                                      <SelectContent className="max-h-64">{catalog.map(c => (<SelectItem key={c.code} value={c.code}>{c.code} — {c.description.slice(0, 45)}</SelectItem>))}</SelectContent>
                                    </Select>
                                    <Button type="button" size="sm" variant="outline" onClick={() => addToRoom(g.key)} data-testid={`wizard-add-btn-${g.key}`}><Plus className="h-3.5 w-3.5" /></Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="flex justify-between items-center font-semibold border-t pt-2">
                        <span>Totale impianto configurato (imponibile)</span>
                        <span className="text-orange-600" data-testid="preview-subtotal">{euro(configTotal)}</span>
                      </div>
                      <p className="text-xs text-gray-400">Il capitolato generale verrà inserito una sola volta in testa alla categoria D. Nessuna modifica è salvata finché non premi “Applica al preventivo”.</p>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2">
          <Button variant="ghost" disabled={step === 0 || applying} onClick={() => setStep(step - 1)} data-testid="wizard-back-btn">Indietro</Button>
          {step < 5 ? (
            <Button disabled={!canProceed || loading} onClick={goNext} data-testid="wizard-next-btn" className="bg-orange-500 hover:bg-orange-600">Avanti</Button>
          ) : (
            <Button disabled={applying || !preview} onClick={handleApply} data-testid="wizard-apply-btn" className="bg-orange-500 hover:bg-orange-600">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />} Applica al preventivo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ElectricalWizardModal;
